/**
 * Vendor drill-down dialog.
 *
 * Tabs:
 *   - POs & GRNs — paginated POs in the global window. Click a PO to expand
 *     its items and related GRNs (each with its own line items).
 *   - Product Sales — vendor-attributed sales aggregated per (product, store)
 *     with brand / category / subcategory filters and a product search box.
 *
 * Mounted once via {@link VendorDetailDialogRoot} inside `AppShell`.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Boxes, ChevronDown, ChevronRight, FileText, Package, Search, Store, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';

type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import {
  MultiCombobox,
  type MultiComboboxOption,
} from '@/components/ui/MultiCombobox';
import { rangeISO, useFilters } from '@/state/filters';
import { useStoresList } from '@/api/legacy/stores';
import {
  closeVendorDetail,
  useVendorDetailDialogStore,
  type VendorDetailDialogTarget,
} from '@/state/vendorDetailDialog';
import {
  useInventoryTaxonomy,
  useVendorPoDetail,
  useVendorPos,
  useVendorProductSales,
} from '@/api/bi';
import type {
  VendorPoDetailResponse,
  VendorPoSummary,
  VendorProductSaleRow,
} from '@/api/bi/types';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

type DialogTab = 'pos' | 'sales';

const PO_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'delivery_pending', label: 'Delivery Pending' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

function poStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'delivered':
    case 'completed':
      return 'success';
    case 'cancelled':
    case 'rejected':
      return 'danger';
    case 'pending':
    case 'in_progress':
    case 'approved':
      return 'info';
    case 'shipped':
    case 'partially_received':
    case 'delivery_pending':
      return 'warning';
    default:
      return 'neutral';
  }
}

function grnStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'accepted':
    case 'verified':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'created':
      return 'info';
    default:
      return 'neutral';
  }
}

function statusLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function VendorHeader({ target }: { target: VendorDetailDialogTarget }) {
  const titleName = (target.vendorName?.trim() || target.vendorCompany?.trim() || `Vendor #${target.vendorId}`);
  const sub = target.vendorName && target.vendorCompany && target.vendorName !== target.vendorCompany
    ? target.vendorCompany
    : null;
  return (
    <div className="border-b border-border px-4 py-3 pr-12">
      <div className="flex items-start gap-2">
        <Package className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-foreground">{titleName}</h2>
          {sub ? (
            <p className="truncate text-[11px] text-muted-foreground">{sub}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// POs & GRNs tab
// ---------------------------------------------------------------------------

function PoRow({
  po,
  expanded,
  onToggle,
}: {
  po: VendorPoSummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <tr
      className={cn(
        'cursor-pointer transition-colors hover:bg-muted/40',
        expanded && 'bg-muted/30',
      )}
      onClick={onToggle}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="kpi-num text-foreground">#{po.id}</span>
        </div>
      </td>
      <td className="px-2 py-2">
        <Badge tone={poStatusTone(po.status)} className="text-[10px]">
          {statusLabel(po.status)}
        </Badge>
        {po.is_late ? (
          <Badge tone="danger" className="ml-1 text-[10px]">late</Badge>
        ) : null}
      </td>
      <td className="hidden px-2 py-2 text-[11px] text-muted-foreground sm:table-cell">
        {fmt.date(po.created_at)}
      </td>
      <td className="hidden px-2 py-2 text-[11px] text-muted-foreground md:table-cell">
        {po.delivery_date ? fmt.date(po.delivery_date) : '—'}
      </td>
      <td className="kpi-num px-2 py-2 text-right tabular-nums">
        {fmt.number(po.item_count)}
      </td>
      <td className="kpi-num px-2 py-2 text-right tabular-nums">
        {fmt.npr(po.ordered_value)}
      </td>
      <td className="kpi-num px-2 py-2 text-right tabular-nums">
        <div>{fmt.npr(po.received_value)}</div>
        {po.fill_rate != null ? (
          <div className="text-[10px] text-muted-foreground">{fmt.pct(po.fill_rate)} fill</div>
        ) : null}
      </td>
      <td className="px-2 py-2 text-right">
        <span className="kpi-num text-xs">{fmt.number(po.grns)}</span>
      </td>
    </tr>
  );
}

function PoExpandedDetail({ poId }: { poId: number }) {
  const detail = useVendorPoDetail(poId, true);

  if (detail.isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="p-4">
        <ErrorState description="Couldn't load PO detail." onRetry={() => void detail.refetch()} />
      </div>
    );
  }

  const data: VendorPoDetailResponse = detail.data;
  return (
    <div className="space-y-3 bg-surface p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground sm:grid-cols-4">
        <div>
          <div className="uppercase tracking-wider opacity-70">Ordered</div>
          <div className="kpi-num text-sm text-foreground">{fmt.npr(data.po.ordered_value)}</div>
          <div className="text-[10px]">{fmt.number(data.po.ordered_units)} units</div>
        </div>
        <div>
          <div className="uppercase tracking-wider opacity-70">Received</div>
          <div className="kpi-num text-sm text-foreground">{fmt.npr(data.po.received_value)}</div>
          <div className="text-[10px]">{fmt.number(data.po.received_units)} units</div>
        </div>
        <div>
          <div className="uppercase tracking-wider opacity-70">Fill rate</div>
          <div className="kpi-num text-sm text-foreground">
            {data.po.fill_rate != null ? fmt.pct(data.po.fill_rate) : '—'}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider opacity-70">Payment</div>
          <div className="text-sm text-foreground">
            {data.po.payment_method ? statusLabel(data.po.payment_method) : '—'}
          </div>
          {data.po.payment_terms ? (
            <div className="truncate text-[10px]">{data.po.payment_terms}</div>
          ) : null}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <FileText className="h-3 w-3" /> PO line items ({data.items.length})
        </div>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-2 py-1.5 font-medium">Product</th>
                <th className="hidden px-2 py-1.5 font-medium md:table-cell">Brand</th>
                <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                <th className="px-2 py-1.5 text-right font-medium">Price</th>
                <th className="px-2 py-1.5 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 text-center text-muted-foreground">
                    No items.
                  </td>
                </tr>
              ) : (
                data.items.map((it) => (
                  <tr key={it.id}>
                    <td className="max-w-[260px] truncate px-2 py-1.5">
                      {it.product_name}
                      {it.subcategory ? (
                        <div className="text-[9px] text-muted-foreground">{it.subcategory}</div>
                      ) : null}
                    </td>
                    <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
                      {it.brand ?? '—'}
                    </td>
                    <td className="kpi-num px-2 py-1.5 text-right tabular-nums">
                      {fmt.number(it.order_quantity)}
                    </td>
                    <td className="kpi-num px-2 py-1.5 text-right tabular-nums">
                      {fmt.npr(it.agreed_price, true)}
                    </td>
                    <td className="kpi-num px-2 py-1.5 text-right tabular-nums">
                      {fmt.npr(it.line_total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Boxes className="h-3 w-3" /> Related GRNs ({data.grns.length})
        </div>
        {data.grns.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-[11px] text-muted-foreground">
            No GRNs received against this PO yet.
          </div>
        ) : (
          <div className="space-y-2">
            {data.grns.map((grn) => (
              <details
                key={grn.id}
                className="group rounded-md border border-border bg-surface-elevated"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-[11px] focus-ring">
                  <div className="flex min-w-0 items-center gap-2">
                    <ChevronRight className="h-3 w-3 text-muted-foreground transition-transform group-open:rotate-90" />
                    <span className="kpi-num truncate font-medium text-foreground">
                      {grn.identifier}
                    </span>
                    <Badge tone={grnStatusTone(grn.status)} className="text-[9px]">
                      {statusLabel(grn.status)}
                    </Badge>
                    {grn.ref_bill_no ? (
                      <span className="hidden truncate text-muted-foreground sm:inline">
                        bill #{grn.ref_bill_no}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-muted-foreground">
                    <span>{fmt.date(grn.created_at)}</span>
                    <span className="kpi-num text-foreground">{fmt.npr(grn.received_value)}</span>
                  </div>
                </summary>
                <div className="overflow-x-auto border-t border-border">
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-muted/30">
                      <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-2 py-1 font-medium">Product</th>
                        <th className="px-2 py-1 text-right font-medium">Recv</th>
                        <th className="px-2 py-1 text-right font-medium">Exp</th>
                        <th className="px-2 py-1 text-right font-medium">Price</th>
                        <th className="px-2 py-1 text-right font-medium">Total</th>
                        <th className="hidden px-2 py-1 text-right font-medium md:table-cell">Expiry</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {grn.items.map((it) => {
                        const short = it.quantity_expected > 0 && it.quantity_received < it.quantity_expected;
                        return (
                          <tr key={it.id}>
                            <td className="max-w-[240px] truncate px-2 py-1">
                              {it.product_name}
                              {it.brand ? (
                                <div className="text-[9px] text-muted-foreground">{it.brand}</div>
                              ) : null}
                            </td>
                            <td className={cn('kpi-num px-2 py-1 text-right tabular-nums', short && 'text-warning')}>
                              {fmt.number(it.quantity_received)}
                            </td>
                            <td className="kpi-num px-2 py-1 text-right tabular-nums text-muted-foreground">
                              {fmt.number(it.quantity_expected)}
                            </td>
                            <td className="kpi-num px-2 py-1 text-right tabular-nums">
                              {fmt.npr(it.price, true)}
                            </td>
                            <td className="kpi-num px-2 py-1 text-right tabular-nums">
                              {fmt.npr(it.line_total)}
                            </td>
                            <td className="hidden px-2 py-1 text-right text-muted-foreground md:table-cell">
                              {it.expiry_date ? fmt.date(it.expiry_date) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PosTab({ vendorId, start, end }: { vendorId: number; start: string; end: string }) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [expandedPo, setExpandedPo] = useState<number | null>(null);
  const pageSize = 20;

  useEffect(() => {
    setPage(1);
    setExpandedPo(null);
  }, [vendorId, start, end, statusFilter, onlyOpen]);

  const query = useVendorPos({
    vendorId,
    start,
    end,
    page,
    pageSize,
    status: statusFilter === 'all' ? null : statusFilter,
    onlyOpen,
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rows = query.data?.rows ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <div className="min-w-[160px] flex-1 sm:flex-none sm:w-44">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PO_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="press flex cursor-pointer select-none items-center gap-1.5 rounded-md border border-input bg-surface px-2 py-1 text-[11px] focus-ring">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(e) => setOnlyOpen(e.target.checked)}
            className="h-3 w-3 accent-primary"
          />
          Open only
        </label>
        <div className="flex-1" />
        {query.data ? (
          <Badge tone="neutral" className="!px-2 text-[10px]">
            {fmt.number(total)} POs
          </Badge>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {query.isError ? (
          <div className="p-4">
            <ErrorState onRetry={() => void query.refetch()} />
          </div>
        ) : query.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No POs in this window for the selected filters.
          </p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 z-[1] border-b border-border bg-surface-elevated">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium">PO</th>
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="hidden px-2 py-2 font-medium sm:table-cell">Created</th>
                <th className="hidden px-2 py-2 font-medium md:table-cell">Delivery</th>
                <th className="px-2 py-2 text-right font-medium">Items</th>
                <th className="px-2 py-2 text-right font-medium">Ordered</th>
                <th className="px-2 py-2 text-right font-medium">Received</th>
                <th className="px-2 py-2 text-right font-medium">GRNs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((po) => (
                <Fragment key={po.id}>
                  <PoRow
                    po={po}
                    expanded={expandedPo === po.id}
                    onToggle={() => setExpandedPo((prev) => (prev === po.id ? null : po.id))}
                  />
                  {expandedPo === po.id ? (
                    <tr>
                      <td colSpan={8} className="border-t border-border p-0">
                        <PoExpandedDetail poId={po.id} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!query.isLoading && !query.isError && total > 0 ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          {totalPages > 1 ? (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Fragment({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Product sales tab
// ---------------------------------------------------------------------------

type SalesView = 'rows' | 'by_product' | 'by_store';

function SalesTab({ vendorId, start, end }: { vendorId: number; start: string; end: string }) {
  const globalStoreIds = useFilters((s) => s.storeIds);
  const globalStoreId = useFilters((s) => s.storeId);
  const taxonomy = useInventoryTaxonomy();
  const stores = useStoresList();

  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  // Local store override — `null` = inherit the global filter; otherwise a
  // single store id picked from the in-dialog combobox.
  const [storeOverride, setStoreOverride] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [view, setView] = useState<SalesView>('by_product');

  useEffect(() => {
    setBrandIds([]);
    setCategoryIds([]);
    setSubcategoryIds([]);
    setStoreOverride(null);
    setQ('');
  }, [vendorId]);

  const effectiveStoreIds: number[] | null = useMemo(() => {
    if (storeOverride != null) return [storeOverride];
    if (globalStoreIds.length) return globalStoreIds;
    if (globalStoreId != null) return [globalStoreId];
    return null;
  }, [storeOverride, globalStoreIds, globalStoreId]);

  const query = useVendorProductSales({
    vendorId,
    start,
    end,
    storeIds: effectiveStoreIds,
    brandIds,
    categoryIds,
    subcategoryIds,
    q,
    limit: 500,
  });

  const totals = query.data?.totals;
  // Cascade: subcategories narrow when one or more categories are picked,
  // mirroring the `TaxonomyRail` cascade so the operator gets the same
  // mental model whether they're inside the vendor dialog or on a stock
  // page.
  const visibleSubcats = useMemo(() => {
    if (!taxonomy.data) return [];
    if (categoryIds.length === 0) return taxonomy.data.subcategories;
    const allowed = new Set(categoryIds);
    return taxonomy.data.subcategories.filter((s) =>
      s.category_ids.some((cid) => allowed.has(cid)),
    );
  }, [taxonomy.data, categoryIds]);

  // Auto-prune subcategory selections when the upstream category list
  // changes — keeps the filter state coherent without callbacks.
  useEffect(() => {
    if (!taxonomy.data) return;
    const valid = new Set(visibleSubcats.map((s) => s.id));
    setSubcategoryIds((cur) => cur.filter((id) => valid.has(id)));
  }, [taxonomy.data, visibleSubcats]);

  const brandOptions: MultiComboboxOption[] = useMemo(
    () => (taxonomy.data?.brands ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    [taxonomy.data],
  );
  const categoryOptions: MultiComboboxOption[] = useMemo(
    () => (taxonomy.data?.categories ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [taxonomy.data],
  );
  const subcategoryOptions: MultiComboboxOption[] = useMemo(
    () => visibleSubcats.map((s) => ({ value: String(s.id), label: s.name })),
    [visibleSubcats],
  );
  const storeOptions: ComboboxOption[] = useMemo(
    () =>
      (stores.data ?? []).map((s) => ({
        value: String(s.id),
        label: s.name,
        hint: s.is_online ? 'online' : 'offline',
      })),
    [stores.data],
  );

  const filterCount =
    brandIds.length
    + categoryIds.length
    + subcategoryIds.length
    + (storeOverride != null ? 1 : 0)
    + (q ? 1 : 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-border bg-surface px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search product…"
              className="h-8 pl-7 pr-7 text-xs"
            />
            {q ? (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>

          <Combobox
            value={storeOverride == null ? null : String(storeOverride)}
            onChange={(v) => setStoreOverride(v == null ? null : Number(v))}
            options={storeOptions}
            placeholder={
              storeOverride != null
                ? undefined
                : globalStoreIds.length
                  ? `Inherits ${globalStoreIds.length} store filter`
                  : 'All stores'
            }
            searchPlaceholder="Search store…"
            clearLabel={globalStoreIds.length ? 'Inherit global filter' : 'All stores'}
            loading={stores.isLoading}
            triggerClassName="h-8 text-xs min-w-[180px]"
            width={240}
          />

          <MultiCombobox
            values={brandIds.map(String)}
            onChange={(v) => setBrandIds(v.map(Number))}
            options={brandOptions}
            placeholder="All brands"
            searchPlaceholder="Search brand…"
            allLabel="All brands"
            countNoun="brand"
            loading={taxonomy.isLoading}
            triggerClassName="h-8 text-xs min-w-[160px]"
            width={220}
          />

          <MultiCombobox
            values={categoryIds.map(String)}
            onChange={(v) => setCategoryIds(v.map(Number))}
            options={categoryOptions}
            placeholder="All categories"
            searchPlaceholder="Search category…"
            allLabel="All categories"
            countNoun="category"
            loading={taxonomy.isLoading}
            triggerClassName="h-8 text-xs min-w-[160px]"
            width={240}
          />

          <MultiCombobox
            values={subcategoryIds.map(String)}
            onChange={(v) => setSubcategoryIds(v.map(Number))}
            options={subcategoryOptions}
            placeholder="All subcategories"
            searchPlaceholder="Search subcategory…"
            allLabel="All subcategories"
            countNoun="sub-cat"
            loading={taxonomy.isLoading}
            triggerClassName="h-8 text-xs min-w-[160px]"
            width={240}
          />

          {filterCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-[11px]"
              onClick={() => {
                setBrandIds([]);
                setCategoryIds([]);
                setSubcategoryIds([]);
                setStoreOverride(null);
                setQ('');
              }}
            >
              Clear ({filterCount})
            </Button>
          ) : null}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
          <span>Group by:</span>
          {(
            [
              { v: 'by_product', label: 'Product' },
              { v: 'by_store', label: 'Store' },
              { v: 'rows', label: 'Product × Store' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setView(opt.v)}
              className={cn(
                'press rounded-full border px-2 py-0.5 font-medium focus-ring',
                view === opt.v
                  ? 'border-primary/30 bg-primary/10 text-primary'
                  : 'border-border bg-surface text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
          {storeOverride != null ? (
            <Badge tone="primary" className="!px-2 text-[10px]">
              <Store className="h-2.5 w-2.5" />
              {stores.data?.find((s) => s.id === storeOverride)?.name ?? `Store #${storeOverride}`}
            </Badge>
          ) : globalStoreIds.length ? (
            <Badge tone="info" className="!px-2 text-[10px]">
              {globalStoreIds.length} global store filter
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 border-b border-border bg-surface-elevated px-3 py-2 text-[11px] sm:grid-cols-4">
        <SalesKpi label="Gross revenue" value={fmt.npr(totals?.gross_revenue ?? 0)} />
        <SalesKpi label="Units" value={fmt.number(totals?.units ?? 0)} />
        <SalesKpi
          label="Margin"
          value={fmt.npr(totals?.margin ?? 0)}
          hint={totals?.margin_pct != null ? `${fmt.pct(totals.margin_pct)} margin` : undefined}
        />
        <SalesKpi
          label="Reach"
          value={`${fmt.number(totals?.products ?? 0)} products`}
          hint={`${fmt.number(totals?.stores ?? 0)} stores`}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {query.isError ? (
          <div className="p-4">
            <ErrorState onRetry={() => void query.refetch()} />
          </div>
        ) : query.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : (
          <SalesTable view={view} data={query.data} />
        )}
      </div>
    </div>
  );
}

function SalesKpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="kpi-num text-sm font-medium text-foreground">{value}</div>
      {hint ? <div className="text-[9px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function SalesTable({
  view,
  data,
}: {
  view: SalesView;
  data: ReturnType<typeof useVendorProductSales>['data'];
}) {
  if (!data) return null;

  if (view === 'by_store') {
    if (data.by_store.length === 0) {
      return <p className="p-4 text-sm text-muted-foreground">No store sales in window.</p>;
    }
    return (
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 z-[1] border-b border-border bg-surface-elevated">
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Store</th>
            <th className="px-2 py-2 text-right font-medium">Products</th>
            <th className="px-2 py-2 text-right font-medium">Units</th>
            <th className="px-2 py-2 text-right font-medium">Gross</th>
            <th className="px-2 py-2 text-right font-medium">Margin</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.by_store.map((s) => (
            <tr key={s.store_id ?? 0}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <Store className="h-3 w-3 text-muted-foreground" />
                  <span className="truncate">{s.store_name ?? `Store #${s.store_id ?? '—'}`}</span>
                </div>
              </td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.number(s.products)}</td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.number(s.units)}</td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.npr(s.gross_revenue)}</td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">
                {fmt.npr(s.margin)}
                {s.margin_pct != null ? (
                  <div className="text-[9px] text-muted-foreground">{fmt.pct(s.margin_pct)}</div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (view === 'by_product') {
    if (data.by_product.length === 0) {
      return <p className="p-4 text-sm text-muted-foreground">No product sales in window.</p>;
    }
    return (
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 z-[1] border-b border-border bg-surface-elevated">
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium">Product</th>
            <th className="hidden px-2 py-2 font-medium md:table-cell">Brand</th>
            <th className="hidden px-2 py-2 font-medium md:table-cell">Subcategory</th>
            <th className="px-2 py-2 text-right font-medium">Units</th>
            <th className="px-2 py-2 text-right font-medium">Gross</th>
            <th className="px-2 py-2 text-right font-medium">Margin</th>
            <th className="hidden px-2 py-2 text-right font-medium sm:table-cell">Stores</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.by_product.map((p) => (
            <tr key={p.product_id ?? Math.random()}>
              <td className="max-w-[260px] truncate px-3 py-2">{p.product_name}</td>
              <td className="hidden truncate px-2 py-2 text-muted-foreground md:table-cell">
                {p.brand ?? '—'}
              </td>
              <td className="hidden truncate px-2 py-2 text-muted-foreground md:table-cell">
                {p.subcategory ?? '—'}
              </td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.number(p.units)}</td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.npr(p.gross_revenue)}</td>
              <td className="kpi-num px-2 py-2 text-right tabular-nums">
                {fmt.npr(p.margin)}
                {p.margin_pct != null ? (
                  <div className="text-[9px] text-muted-foreground">{fmt.pct(p.margin_pct)}</div>
                ) : null}
              </td>
              <td className="hidden px-2 py-2 text-right text-muted-foreground sm:table-cell">
                {fmt.number(p.stores)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // rows view (Product × Store)
  if (data.rows.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No sales in window.</p>;
  }
  return (
    <table className="w-full text-left text-xs">
      <thead className="sticky top-0 z-[1] border-b border-border bg-surface-elevated">
        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-2 font-medium">Product</th>
          <th className="px-2 py-2 font-medium">Store</th>
          <th className="hidden px-2 py-2 font-medium md:table-cell">Brand</th>
          <th className="px-2 py-2 text-right font-medium">Units</th>
          <th className="px-2 py-2 text-right font-medium">Gross</th>
          <th className="hidden px-2 py-2 text-right font-medium md:table-cell">Margin</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {data.rows.map((r: VendorProductSaleRow, idx) => (
          <tr key={`${r.product_id ?? 'x'}-${r.store_id ?? 'x'}-${idx}`}>
            <td className="max-w-[220px] truncate px-3 py-2">{r.product_name}</td>
            <td className="max-w-[140px] truncate px-2 py-2 text-muted-foreground">
              {r.store_name ?? '—'}
            </td>
            <td className="hidden truncate px-2 py-2 text-muted-foreground md:table-cell">
              {r.brand ?? '—'}
            </td>
            <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.number(r.units)}</td>
            <td className="kpi-num px-2 py-2 text-right tabular-nums">{fmt.npr(r.gross_revenue)}</td>
            <td className="hidden px-2 py-2 text-right md:table-cell">
              <span className="kpi-num">{fmt.npr(r.margin)}</span>
              {r.margin_pct != null ? (
                <div className="text-[9px] text-muted-foreground">{fmt.pct(r.margin_pct)}</div>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Dialog body
// ---------------------------------------------------------------------------

function VendorDetailDialogBody({ target }: { target: VendorDetailDialogTarget }) {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start, end } = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const [tab, setTab] = useState<DialogTab>(target.tab ?? 'pos');

  return (
    <>
      <VendorHeader target={target} />
      <Tabs value={tab} onValueChange={(v) => setTab(v as DialogTab)} className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-border px-3 py-2">
          <TabsList>
            <TabsTrigger value="pos">POs &amp; GRNs</TabsTrigger>
            <TabsTrigger value="sales">Product Sales</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="pos" className="flex min-h-0 flex-1 flex-col outline-none">
          <PosTab vendorId={target.vendorId} start={start} end={end} />
        </TabsContent>
        <TabsContent value="sales" className="flex min-h-0 flex-1 flex-col outline-none">
          <SalesTab vendorId={target.vendorId} start={start} end={end} />
        </TabsContent>
      </Tabs>
    </>
  );
}

/**
 * Subscribes to {@link useVendorDetailDialogStore} and mounts the modal.
 * Place once inside `AppShell` (needs router + filter context).
 */
export function VendorDetailDialogRoot() {
  const open = useVendorDetailDialogStore((s) => s.open);
  const target = useVendorDetailDialogStore((s) => s.target);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeVendorDetail();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[min(90vh,920px)] w-[calc(100vw-1.5rem)] max-w-5xl flex-col overflow-hidden p-0',
          'gap-0 sm:max-w-5xl',
        )}
      >
        <DialogTitle className="sr-only">Vendor detail</DialogTitle>
        {target ? (
          <VendorDetailDialogBody key={target.vendorId} target={target} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
